import { useCallback, useEffect, useState, useMemo } from "react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";
import {
  Coins,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Info,
  Sparkles,
  Save,
  X,
} from "lucide-react";

export default function Currencies() {
  const [currencies, setCurrencies] = useState([]);
  const [coinPacks, setCoinPacks] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Navigation Tabs: 'coinPacks', 'currencies'
  const [activeTab, setActiveTab] = useState("coinPacks");

  // Multi-delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);

  // Modal Dialog CRUD states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(""); // 'currency', 'coinPack'
  const [editingId, setEditingId] = useState(null);

  // Form schemas
  const [currencyForm, setCurrencyForm] = useState({
    name: "",
    displayName: "",
    inrPrice: 1.0,
    isActive: true,
  });

  const [coinPackForm, setCoinPackForm] = useState({
    title: "Welcome Offer",
    discountTag: "82% Discount",
    coins: 55,
    priceInr: 9,
    offerText: "Get a FREE spin & win up to ₹10,000!",
    isActive: true,
  });

  // Simulator State
  const [simPackId, setSimPackId] = useState("");

  // Fetch configurations
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [resCur, resCoin] = await Promise.all([
        API.get("/admin/currencies"),
        API.get("/admin/coin-packs"),
      ]);

      const fetchedCur = resCur.data?.currencies || [];
      const fetchedCoin = resCoin.data?.coinPacks || [];

      setCurrencies(fetchedCur);
      setCoinPacks(fetchedCoin);

      if (fetchedCoin.length > 0 && !simPackId) {
        setSimPackId(fetchedCoin[0]._id);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load monetization data");
    } finally {
      setLoading(false);
    }
  }, [simPackId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Current items for active tab
  const currentItems = activeTab === "coinPacks" ? coinPacks : currencies;
  const currentEntity = activeTab === "coinPacks" ? "coinPacks" : "currencies";
  const currentEntityName = activeTab === "coinPacks" ? "coin packages" : "currencies";

  const handleToggleSelectAll = () => {
    if (selectedIds.length === currentItems.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(currentItems.map((item) => item._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleBatchDelete = async () => {
    try {
      setIsBatchDeleting(true);
      const { data } = await API.post("/admin/batch-delete", {
        entity: currentEntity,
        ids: selectedIds,
      });
      if (data?.success) {
        setSelectedIds([]);
        setSuccess("Batch delete completed");
        fetchData();
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Batch delete failed");
    } finally {
      setIsBatchDeleting(false);
    }
  };

  // Open Edit / Create Modal
  const openCreateModal = (type) => {
    setError("");
    setSuccess("");
    setModalType(type);
    setEditingId(null);

    if (type === "currency") {
      setCurrencyForm({ name: "", displayName: "", inrPrice: 1.0, isActive: true });
    } else if (type === "coinPack") {
      setCoinPackForm({
        title: "Special Package",
        discountTag: "10% Discount",
        coins: 100,
        priceInr: 50,
        offerText: "Get a FREE spin & win up to ₹10,000!",
        isActive: true,
      });
    }
    setIsModalOpen(true);
  };

  const openEditModal = (type, item) => {
    setError("");
    setSuccess("");
    setModalType(type);
    setEditingId(item._id);

    if (type === "currency") {
      setCurrencyForm({
        name: item.name,
        displayName: item.displayName,
        inrPrice: item.inrPrice,
        isActive: item.isActive,
      });
    } else if (type === "coinPack") {
      setCoinPackForm({
        title: item.title || "Coin Package",
        discountTag: item.discountTag || "",
        coins: item.coins,
        priceInr: item.priceInr,
        offerText: item.offerText || "",
        isActive: item.isActive,
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setModalType("");
  };

  // Submit Handler for Creation & Update
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      let response;
      if (modalType === "currency") {
        if (editingId) {
          response = await API.put(`/admin/currencies/${editingId}`, currencyForm);
        } else {
          response = await API.post("/admin/currencies", currencyForm);
        }
      } else if (modalType === "coinPack") {
        if (editingId) {
          response = await API.put(`/admin/coin-packs/${editingId}`, coinPackForm);
        } else {
          response = await API.post("/admin/coin-packs", coinPackForm);
        }
      }

      setSuccess(response.data?.message || "Saved successfully");
      closeModal();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || "Operation failed");
    }
  };

  // Delete Item
  const handleDelete = async (type, id, title) => {
    if (!window.confirm(`Are you sure you want to delete '${title}'?`)) return;

    setError("");
    setSuccess("");

    try {
      let endpoint = "";
      if (type === "currency") endpoint = `/admin/currencies/${id}`;
      else if (type === "coinPack") endpoint = `/admin/coin-packs/${id}`;

      const response = await API.delete(endpoint);
      setSuccess(response.data?.message || "Deleted successfully");
      setSelectedIds((prev) => prev.filter((i) => i !== id));
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || "Deletion failed");
    }
  };

  // Toggle active status inline
  const handleToggleActive = async (type, item) => {
    setError("");
    setSuccess("");
    try {
      if (type === "currency") {
        await API.put(`/admin/currencies/${item._id}`, { isActive: !item.isActive });
      } else if (type === "coinPack") {
        await API.put(`/admin/coin-packs/${item._id}`, { isActive: !item.isActive });
      }
      setSuccess(`Status for '${item.displayName || (item.coins ? item.coins + " Coins" : "Package")}' updated`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update status");
    }
  };

  // Purchase Simulator Calculations
  const simPurchaseResult = useMemo(() => {
    if (!simPackId) return null;
    const selected = coinPacks.find((p) => p._id === simPackId);
    if (!selected) return null;
    const perCoinCost = selected.priceInr / selected.coins;
    return {
      coins: selected.coins,
      price: selected.priceInr,
      perCoinCost: perCoinCost.toFixed(2),
    };
  }, [simPackId, coinPacks]);

  return (
    <div className="space-y-6 text-gray-800 dark:text-gray-100 lg:pl-5 xl:pl-7 2xl:pl-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
            Coin Purchase Packages & Pricing
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Define coin purchase packages and coin pricing for app users.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 transition-colors"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>

          <button
            onClick={() => {
              if (activeTab === "coinPacks") openCreateModal("coinPack");
              else openCreateModal("currency");
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors shadow-lg shadow-blue-500/20"
          >
            <Plus size={16} />
            {activeTab === "coinPacks" ? "Add Purchase Package" : "Add Currency"}
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="p-3.5 bg-red-50 text-red-700 border border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/20 rounded-xl text-sm flex items-center gap-2">
          <XCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="p-3.5 bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20 rounded-xl text-sm flex items-center gap-2">
          <CheckCircle size={18} />
          <span>{success}</span>
        </div>
      )}

      {/* Purchase Deal Quick Summary & Live Mobile App Preview Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Purchase Pack Deal Calculator & Overview */}
        <div className="lg:col-span-1 space-y-6 flex flex-col justify-between">
          <div className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border border-yellow-200/50 dark:border-yellow-500/20 rounded-2xl p-5">
            <div className="flex justify-between items-start">
              <span className="text-[10px] uppercase font-bold text-amber-600 dark:text-amber-400 tracking-wider">
                Coin Pack Rate Calculator
              </span>
              <Coins className="text-amber-500" size={20} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mt-1">Buy Coins Pack</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-[11px] text-gray-400 uppercase font-semibold mb-1">
                  Select Pack Deal
                </label>
                <select
                  value={simPackId}
                  onChange={(e) => setSimPackId(e.target.value)}
                  className="w-full rounded-lg border border-yellow-300/40 bg-white/70 dark:bg-slate-900/50 px-2.5 py-1.5 text-xs outline-none focus:ring-1 focus:ring-amber-500"
                >
                  {coinPacks.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.title || "Pack"} ({p.coins} Coins for ₹{p.priceInr})
                    </option>
                  ))}
                </select>
              </div>

              {simPurchaseResult && (
                <div className="p-2.5 rounded-xl bg-white/80 dark:bg-slate-900/80 border border-yellow-100 dark:border-slate-800 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Price:</span>
                    <span className="font-bold text-amber-700 dark:text-amber-400">
                      ₹{simPurchaseResult.price}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Per Coin Cost:</span>
                    <span className="font-semibold">₹{simPurchaseResult.perCoinCost} / Coin</span>
                  </div>
                </div>
              )}
            </div>
            <div className="text-[10px] text-gray-400 mt-3 flex items-center gap-1">
              <Info size={12} /> Syncs directly with mobile app coin purchase screen
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-2xl p-5 space-y-4">
            <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
              Quick Overview
            </span>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-100 dark:border-slate-700/50">
                <p className="text-[10px] text-gray-400 font-semibold uppercase">Purchase Packages</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{coinPacks.length}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-100 dark:border-slate-700/50">
                <p className="text-[10px] text-gray-400 font-semibold uppercase">Currencies</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{currencies.length}</p>
              </div>
            </div>
            <div className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold flex items-center gap-1">
              <Sparkles size={14} /> Active coin purchase packages configured
            </div>
          </div>
        </div>

        {/* Right: Mobile App Live Display Preview */}
        <div className="lg:col-span-2 bg-[#10172a] rounded-3xl p-6 border border-slate-700/80 shadow-2xl text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
              <h3 className="text-lg font-extrabold tracking-tight text-slate-100">
                Recommended Packs
              </h3>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400 font-medium">
              Live App Screen View
            </span>
          </div>

          {coinPacks.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              No coin packages added yet. Click "Add Purchase Package" above to create one.
            </div>
          ) : (
            <div className="space-y-3.5 max-h-[460px] overflow-y-auto pr-1">
              {coinPacks.map((pack) => (
                <div
                  key={pack._id}
                  className={`rounded-2xl p-4 transition-all ${
                    pack.isActive
                      ? "bg-[#18213e]/90 border border-slate-600/60 shadow-lg"
                      : "bg-[#18213e]/40 border border-slate-700/40 opacity-60"
                  }`}
                >
                  {/* Top Header: Title & Discount Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-bold text-white tracking-wide">
                      {pack.title || "Coin Package"}
                    </h4>
                    {pack.discountTag && (
                      <span className="px-3 py-0.5 rounded-full text-xs font-semibold text-blue-200 border border-blue-400/40 bg-blue-900/30">
                        {pack.discountTag}
                      </span>
                    )}
                  </div>

                  {/* Middle Row: Coins & Price Button */}
                  <div className="flex items-center justify-between my-2">
                    <div className="flex items-center gap-2.5">
                      {/* Golden Coin Icon */}
                      <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-amber-500 to-yellow-300 flex items-center justify-center shadow-md border border-yellow-200 text-amber-950 font-black text-sm italic">
                        T
                      </div>
                      <span className="text-3xl font-extrabold text-white tracking-tight">
                        {pack.coins}
                      </span>
                    </div>

                    <button className="bg-[#ffbd30] hover:bg-[#ffa712] text-amber-950 font-black px-5 py-2 rounded-xl text-base shadow-lg transition-transform active:scale-95">
                      ₹{pack.priceInr}
                    </button>
                  </div>

                  {/* Bottom Sub-Banner Offer if available */}
                  {pack.offerText && (
                    <div className="mt-3.5 bg-gradient-to-r from-[#3f1f53] to-[#4c1d68] border border-purple-400/30 rounded-xl p-2.5 flex items-center justify-center gap-2 text-xs font-semibold text-purple-100">
                      <span className="text-sm">🎡</span>
                      <span>{pack.offerText}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabs navigation */}
      <div className="flex border-b border-gray-200 dark:border-slate-700 mt-6">
        <button
          onClick={() => {
            setActiveTab("coinPacks");
            setSelectedIds([]);
          }}
          className={`flex items-center gap-2 px-5 py-3 border-b-2 text-sm font-semibold transition-all ${
            activeTab === "coinPacks"
              ? "border-blue-600 text-blue-600 dark:text-blue-400"
              : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          <Coins size={16} />
          Coin Purchase Packages
        </button>
        <button
          onClick={() => {
            setActiveTab("currencies");
            setSelectedIds([]);
          }}
          className={`flex items-center gap-2 px-5 py-3 border-b-2 text-sm font-semibold transition-all ${
            activeTab === "currencies"
              ? "border-blue-600 text-blue-600 dark:text-blue-400"
              : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          <Sparkles size={16} />
          System Currencies
        </button>
      </div>

      {/* Active Tab Panel Content */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-5 space-y-4">
        {/* Tab 1: Coin Purchase Packs */}
        {activeTab === "coinPacks" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Coin Purchase Packages</h2>
              <span className="text-xs text-gray-400">{coinPacks.length} packages</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs font-semibold uppercase text-gray-400 border-b border-gray-100 dark:border-slate-700">
                    <th className="py-3.5 xl:py-4 px-3 xl:px-4 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={coinPacks.length > 0 && selectedIds.length === coinPacks.length}
                        onChange={handleToggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">Package Title</th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">Badge / Discount</th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">Coins Offered</th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">INR Price</th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">Special Offer Banner</th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">Status</th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700 text-sm">
                  {coinPacks.map((pack) => {
                    const isSelected = selectedIds.includes(pack._id);
                    return (
                    <tr key={pack._id} className={`transition-colors ${isSelected ? "bg-blue-50/70 dark:bg-blue-500/10" : "hover:bg-slate-50/50 dark:hover:bg-slate-700/20"}`}>
                      <td className="py-3.5 xl:py-4 px-3 xl:px-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(pack._id)}
                          className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 font-bold text-gray-900 dark:text-white">
                        {pack.title || "Coin Package"}
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">
                        {pack.discountTag ? (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold border border-purple-400/40 text-purple-600 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/30">
                            {pack.discountTag}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 font-semibold text-amber-600 dark:text-amber-400">
                        {pack.coins} Coins
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 font-bold text-emerald-600">₹{pack.priceInr}</td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-xs text-gray-600 dark:text-gray-300 max-w-xs truncate">
                        {pack.offerText || <span className="text-gray-400">-</span>}
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">
                        <button
                          onClick={() => handleToggleActive("coinPack", pack)}
                          className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                            pack.isActive
                              ? "bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400"
                          }`}
                        >
                          {pack.isActive ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-right space-x-1">
                        <button
                          onClick={() => openEditModal("coinPack", pack)}
                          className="p-1 px-2.5 rounded bg-slate-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-slate-200 transition-colors inline-flex items-center gap-1 text-xs"
                        >
                          <Edit2 size={12} /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete("coinPack", pack._id, pack.title || `${pack.coins} Coins Pack`)}
                          className="p-1 px-2.5 rounded bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-300 hover:bg-red-100 transition-colors inline-flex items-center gap-1 text-xs"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 2: System Currencies */}
        {activeTab === "currencies" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">System Currencies</h2>
              <span className="text-xs text-gray-400">{currencies.length} currencies</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs font-semibold uppercase text-gray-400 border-b border-gray-100 dark:border-slate-700">
                    <th className="py-3.5 xl:py-4 px-3 xl:px-4 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={currencies.length > 0 && selectedIds.length === currencies.length}
                        onChange={handleToggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">Currency Name</th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">Key ID</th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">INR Price (1 unit)</th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">Status</th>
                    <th className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700 text-sm">
                  {currencies.map((currency) => {
                    const isSelected = selectedIds.includes(currency._id);
                    return (
                    <tr key={currency._id} className={`transition-colors ${isSelected ? "bg-blue-50/70 dark:bg-blue-500/10" : "hover:bg-slate-50/50 dark:hover:bg-slate-700/20"}`}>
                      <td className="py-3.5 xl:py-4 px-3 xl:px-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(currency._id)}
                          className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 font-semibold text-gray-900 dark:text-white">
                        {currency.displayName}
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 font-mono text-xs text-gray-500">{currency.name}</td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 font-bold text-emerald-600 dark:text-emerald-400">
                        ₹{currency.inrPrice.toFixed(2)}
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6">
                        <button
                          onClick={() => handleToggleActive("currency", currency)}
                          className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                            currency.isActive
                              ? "bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400"
                              : "bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400"
                          }`}
                        >
                          {currency.isActive ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="py-3.5 xl:py-4 px-3.5 xl:px-4 2xl:px-6 text-right space-x-1">
                        <button
                          onClick={() => openEditModal("currency", currency)}
                          className="p-1 px-2.5 rounded bg-slate-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-slate-200 transition-colors inline-flex items-center gap-1 text-xs"
                        >
                          <Edit2 size={12} /> Edit
                        </button>
                        {currency.name !== "coin" && (
                          <button
                            onClick={() => handleDelete("currency", currency._id, currency.displayName)}
                            className="p-1 px-2.5 rounded bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-300 hover:bg-red-100 transition-colors inline-flex items-center gap-1 text-xs"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Floating Multi-Delete Bar */}
      <BatchActionBar
        selectedCount={selectedIds.length}
        totalCount={currentItems.length}
        onClearSelection={() => setSelectedIds([])}
        onSelectAll={handleToggleSelectAll}
        onDeleteSelected={handleBatchDelete}
        isDeleting={isBatchDeleting}
        entityName={currentEntityName}
      />

      {/* CRUD MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-700 pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingId ? "Edit" : "Add"}{" "}
                {modalType === "currency" ? "System Currency" : "Coin Purchase Package"}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-sm">
              {modalType === "currency" && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      required
                      value={currencyForm.displayName}
                      onChange={(e) => setCurrencyForm({ ...currencyForm, displayName: e.target.value })}
                      placeholder="e.g. Coin"
                      className="w-full px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  {!editingId && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        Unique Name Key
                      </label>
                      <input
                        type="text"
                        required
                        value={currencyForm.name}
                        onChange={(e) => setCurrencyForm({ ...currencyForm, name: e.target.value })}
                        placeholder="e.g. coin"
                        className="w-full px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                      INR Price (1 unit)
                    </label>
                    <input
                      type="number"
                      step="any"
                      required
                      value={currencyForm.inrPrice}
                      onChange={(e) =>
                        setCurrencyForm({ ...currencyForm, inrPrice: parseFloat(e.target.value) })
                      }
                      className="w-full px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>
                </>
              )}

              {modalType === "coinPack" && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                      Package Title / Name
                    </label>
                    <input
                      type="text"
                      required
                      value={coinPackForm.title}
                      onChange={(e) =>
                        setCoinPackForm({ ...coinPackForm, title: e.target.value })
                      }
                      placeholder="e.g. Welcome Offer, Prime Package"
                      className="w-full px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                      Discount Badge / Pill Text
                    </label>
                    <input
                      type="text"
                      value={coinPackForm.discountTag}
                      onChange={(e) =>
                        setCoinPackForm({ ...coinPackForm, discountTag: e.target.value })
                      }
                      placeholder="e.g. 82% Discount, 20% Discount"
                      className="w-full px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        Coins Offered
                      </label>
                      <input
                        type="number"
                        required
                        min="1"
                        value={coinPackForm.coins}
                        onChange={(e) =>
                          setCoinPackForm({ ...coinPackForm, coins: parseInt(e.target.value, 10) || 0 })
                        }
                        className="w-full px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        Price (₹ INR)
                      </label>
                      <input
                        type="number"
                        step="any"
                        required
                        min="0"
                        value={coinPackForm.priceInr}
                        onChange={(e) =>
                          setCoinPackForm({ ...coinPackForm, priceInr: parseFloat(e.target.value) || 0 })
                        }
                        className="w-full px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
                      Sub-Banner Offer Text (Spin Wheel / Promo)
                    </label>
                    <input
                      type="text"
                      value={coinPackForm.offerText}
                      onChange={(e) =>
                        setCoinPackForm({ ...coinPackForm, offerText: e.target.value })
                      }
                      placeholder="e.g. Get a FREE spin & win up to ₹10,000!"
                      className="w-full px-3.5 py-2 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>
                </>
              )}

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={
                    modalType === "currency" ? currencyForm.isActive : coinPackForm.isActive
                  }
                  onChange={(e) => {
                    const val = e.target.checked;
                    if (modalType === "currency") setCurrencyForm({ ...currencyForm, isActive: val });
                    else setCoinPackForm({ ...coinPackForm, isActive: val });
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="isActive" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Active status (Visible in app)
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 dark:border-slate-700">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-md inline-flex items-center gap-1.5"
                >
                  <Save size={16} /> Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
